import { useState, useRef, useEffect, useMemo } from 'react';
import { IconCheck, IconSearch } from '@tabler/icons-react';

/**
 * Searchable single-select dropdown (id-based). Like SelectField but with a type-
 * to-filter box — meant for long catalogs (e.g. the ~190 academic subjects).
 *
 * Props:
 *   value            - selected option value (string; a gov ID)
 *   onChange         - called with the new value string
 *   options          - [{ value, label, keywords? }] (keywords: extra words the filter also matches)
 *   triggerClassName - classes on the trigger button
 *   placeholder      - shown when nothing is selected
 */
export default function ComboSelect({ value, onChange, options = [], triggerClassName, placeholder }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0, above: false });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);

    const selectedLabel = options.find((o) => String(o.value) === String(value))?.label;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q) || (o.keywords || []).some((k) => String(k).toLowerCase().includes(q)));
    }, [query, options]);

    const computePos = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const menuH = 300;
        const above = window.innerHeight - rect.bottom < menuH + 8;
        setPos({ top: above ? rect.top - 4 : rect.bottom + 4, left: rect.left, width: rect.width, above });
    };

    const openMenu = () => { computePos(); setQuery(''); setOpen(true); };

    useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
        const close = (e) => {
            if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const reposition = () => computePos();
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close);
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('touchstart', close);
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open]);

    const pick = (v) => { onChange(v); setOpen(false); };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => (open ? setOpen(false) : openMenu())}
                className={`${triggerClassName ?? 'filter-field'} w-full text-left flex items-center`}
                style={{ cursor: 'pointer', paddingRight: 34, lineHeight: 1.5 }}
            >
                <span className={`truncate flex-1 min-w-0 ${selectedLabel ? '' : 'text-zinc-400'}`}>
                    {selectedLabel || placeholder || ''}
                </span>
                <svg
                    style={{ position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, opacity: 0.5, transition: 'transform 0.15s ease' }}
                    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div
                    ref={menuRef}
                    style={{
                        position: 'fixed',
                        top: pos.above ? 'auto' : pos.top,
                        bottom: pos.above ? window.innerHeight - pos.top : 'auto',
                        left: pos.left,
                        width: Math.max(pos.width, 200),
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-xl flex flex-col"
                >
                    <div className="p-1.5 border-b border-zinc-100 dark:border-zinc-800">
                        <div className="relative">
                            <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="..."
                                className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: 240, overflowY: 'auto' }} className="py-1">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-zinc-400">—</div>
                        ) : filtered.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => pick(opt.value)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-left"
                            >
                                <span className="w-4 flex-shrink-0" style={{ color: '#f97316' }}>
                                    {String(opt.value) === String(value) && <IconCheck size={14} />}
                                </span>
                                <span className="truncate">{opt.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
